import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAppointments, getProfessionals, getServices } from '../services/api';
import Icon from '../components/Icon';
import './Dashboard.css';

// Local YYYY-MM-DD for "today" (agenda keys by local date).
function todayISO() {
  const d = new Date();
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tz).toISOString().split('T')[0];
}

function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [appointments, setAppointments] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // NER-3832 — filtro por obra social del listado de próximos turnos (vertical=medical).
  const [coverageFilter, setCoverageFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [apptRes, profRes, svcRes] = await Promise.all([
        getAppointments(todayISO()),
        getProfessionals(),
        getServices(),
      ]);
      setAppointments(apptRes?.appointments || []);
      setProfessionals(profRes?.professionals || []);
      setServices(svcRes?.services || []);
    } catch (err) {
      console.error('[Dashboard] Error loading dashboard:', err);
      setError(t('dashboard.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <div className="loading">{t('dashboard.loadingStats')}</div>;
  }

  // Active = everything except explicitly cancelled.
  const active = appointments.filter((a) => a.status !== 'cancelled');
  const confirmedCount = appointments.filter((a) => a.status === 'confirmed').length;
  const pendingCount = appointments.filter((a) => a.status === 'pending').length;

  const profName = (id) => {
    const p = professionals.find((x) => (x.professionalId || x.id) === id);
    return p ? p.name || p.fullName : t('agenda.unassigned');
  };

  const upcoming = [...active].sort((a, b) => (a.time || '').localeCompare(b.time || ''));

  // ── NER-3832 · Extensiones vertical=medical ─────────────────────────────────
  // El org es "medical" si el signup redirigió con ?vertical=medical o si algún turno
  // del día trae cobertura (obra social) — GET /appointments proyecta coverageKey/
  // coverageName por turno (NER-3839). Sin señal medical, la UI queda idéntica a Citame.
  const NONE_COVERAGE = '__none__';
  const coverageKeyOf = (a) => a.coverageKey || NONE_COVERAGE;
  const coverageLabelOf = (a) => a.coverageName || a.coverageKey || t('dashboard.noCoverageLabel');
  const hasCoverageData = active.some((a) => a.coverageKey);
  const isMedical = searchParams.get('vertical') === 'medical' || hasCoverageData;

  // Conteo de turnos activos de hoy por obra social (card "Cobertura activa").
  const byCoverage = isMedical
    ? Object.values(
        active.reduce((acc, a) => {
          const key = coverageKeyOf(a);
          if (!acc[key]) acc[key] = { key, label: coverageLabelOf(a), count: 0 };
          acc[key].count += 1;
          return acc;
        }, {})
      ).sort((x, y) => y.count - x.count)
    : [];

  // Opciones + filtrado del listado de próximos turnos por cobertura.
  const coverageFilterValid =
    coverageFilter === 'all' || byCoverage.some((c) => c.key === coverageFilter);
  const filteredUpcoming =
    !isMedical || !coverageFilterValid || coverageFilter === 'all'
      ? upcoming
      : upcoming.filter((a) => coverageKeyOf(a) === coverageFilter);

  const perProfessional = professionals
    .map((p) => {
      const id = p.professionalId || p.id;
      return {
        id,
        name: p.name || p.fullName || t('agenda.unnamedProfessional'),
        count: active.filter((a) => a.professionalId === id).length,
      };
    })
    .sort((a, b) => b.count - a.count);

  return (
    <div className="dashboard">
      <div className="page-header">
        <div className="header-content">
          <h1>{t('dashboard.title')}</h1>
          <p>{t('dashboard.subtitle')}</p>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Stat cards */}
      <div className="stats-grid">
        <div className="stat-card primary">
          <div className="stat-icon">
            <Icon name="event_available" size="large" />
          </div>
          <div className="stat-info">
            <h2>{active.length}</h2>
            <p>{t('dashboard.bookingsToday')}</p>
            <span className="stat-hint">
              {t('dashboard.confirmedPending', {
                confirmed: confirmedCount,
                pending: pendingCount,
              })}
            </span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <Icon name="task_alt" size="large" />
          </div>
          <div className="stat-info">
            <h2>{confirmedCount}</h2>
            <p>{t('dashboard.confirmedToday')}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <Icon name="badge" size="large" />
          </div>
          <div className="stat-info">
            <h2>{professionals.length}</h2>
            <p>{t('dashboard.professionals')}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <Icon name="medical_services" size="large" />
          </div>
          <div className="stat-info">
            <h2>{services.length}</h2>
            <p>{t('dashboard.services')}</p>
          </div>
        </div>
      </div>

      <div className="stats-row">
        {/* Upcoming appointments today */}
        <div className="card list-card">
          <div className="list-card-header">
            <h2>{t('dashboard.upcomingToday')}</h2>
            {/* NER-3832 — filtro por obra social (sólo vertical=medical con coberturas). */}
            {isMedical && byCoverage.length > 1 && (
              <div className="dashboard-coverage-filter">
                <label htmlFor="dashCoverageFilter" className="sr-only">
                  {t('dashboard.filterByCoverage')}
                </label>
                <select
                  id="dashCoverageFilter"
                  value={coverageFilter}
                  onChange={(e) => setCoverageFilter(e.target.value)}
                  aria-label={t('dashboard.filterByCoverage')}
                >
                  <option value="all">{t('dashboard.allCoverages')}</option>
                  {byCoverage.map((c) => (
                    <option key={c.key} value={c.key}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          {filteredUpcoming.length > 0 ? (
            <div className="top-products-list">
              {filteredUpcoming.slice(0, 8).map((a) => (
                <div
                  key={a.appointmentId || `${a.time}-${a.clientPhone}`}
                  className="top-product-item"
                >
                  <span className="product-rank">{a.time || '--:--'}</span>
                  <div className="product-info">
                    <span className="product-name">
                      {a.clientName || a.clientPhone || t('agenda.unknownClient')}
                    </span>
                    <span className="product-stats">
                      {[
                        a.serviceName,
                        profName(a.professionalId),
                        isMedical && a.coverageKey ? coverageLabelOf(a) : null,
                      ]
                        .filter(Boolean)
                        .join(' • ')}
                    </span>
                  </div>
                  <span className={`agenda-status ${a.status || ''}`}>
                    {t(`agenda.status.${a.status}`, a.status || '')}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-data">{t('dashboard.noUpcomingToday')}</p>
          )}
        </div>

        {/* NER-3832 — Cobertura activa (turnos de hoy por obra social, vertical=medical) */}
        {isMedical && (
          <div className="card list-card">
            <h2>{t('dashboard.coverageActive')}</h2>
            {byCoverage.length > 0 ? (
              <div className="top-products-list">
                {byCoverage.map((c) => (
                  <div key={c.key} className="top-product-item">
                    <div className="product-info">
                      <span className="product-name">{c.label}</span>
                    </div>
                    <span className="product-stats">
                      {t('dashboard.apptCount', { count: c.count })}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-data">{t('dashboard.noCoverageData')}</p>
            )}
          </div>
        )}

        {/* Bookings per professional */}
        <div className="card list-card">
          <h2>{t('dashboard.byProfessional')}</h2>
          {perProfessional.length > 0 ? (
            <div className="top-products-list">
              {perProfessional.map((p) => (
                <div key={p.id} className="top-product-item">
                  <div className="product-info">
                    <span className="product-name">{p.name}</span>
                  </div>
                  <span className="product-stats">
                    {t('dashboard.apptCount', { count: p.count })}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-data">{t('dashboard.noProfessionals')}</p>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card">
        <h2>{t('dashboard.quickActions')}</h2>
        <div className="quick-actions">
          <button type="button" className="btn btn-primary" onClick={() => navigate('/agenda')}>
            <Icon name="calendar_month" /> {t('dashboard.viewAgenda')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/professionals')}
          >
            <Icon name="badge" /> {t('dashboard.professionals')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/services')}
          >
            <Icon name="medical_services" /> {t('dashboard.services')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
